import type {
  CustomerOrderDetail,
  OrderCancelRequestKind,
  OrderCancelRequestStatus,
} from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { formatCalendarDate, formatDateTime } from '@/shared/lib';

interface OrderCancelRequestsProps {
  readonly requests: CustomerOrderDetail['cancelRequests'];
}

const KIND_LABELS: Readonly<Record<OrderCancelRequestKind, string>> = {
  CUSTOMER: '고객 취소 요청',
  SUPPLY_FAILURE: '공급 불가 취소',
};
const STATUS_LABELS: Readonly<Record<OrderCancelRequestStatus, string>> = {
  REQUESTED: '처리 중',
  ACCEPTED: '환불 완료',
  REJECTED: '반려',
};

interface DetailRow {
  readonly label: string;
  readonly value: ReactNode;
}

const rowsOf = (request: CustomerOrderDetail['cancelRequests'][number]): DetailRow[] => [
  { label: '사유', value: <span className="whitespace-pre-wrap">{request.reason}</span> },
  {
    label: '요청 일시',
    value: <span className="tabular-nums">{formatDateTime(request.requestedAt)}</span>,
  },
  {
    label: '환급 기한',
    value: <span className="tabular-nums">{formatCalendarDate(request.refundDueDate)}</span>,
  },
  ...(request.rejectReason !== null
    ? [
        {
          label: '반려 사유',
          value: <span className="whitespace-pre-wrap">{request.rejectReason}</span>,
        },
      ]
    : []),
  ...(request.resolvedAt !== null
    ? [
        {
          label: '처리 일시',
          value: <span className="tabular-nums">{formatDateTime(request.resolvedAt)}</span>,
        },
      ]
    : []),
];

/** 취소 요청 내역 — 종류와 처리 상태(배지 칩), 사유·요청 일시·환급 기한·반려 사유·처리 일시. 결제 정보와 같은 이름/값 줄. */
export const OrderCancelRequests = ({ requests }: OrderCancelRequestsProps): ReactElement => (
  <section aria-label="취소 요청 내역" className="flex flex-col gap-3">
    <p
      aria-hidden
      className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
    >
      Cancellation
    </p>
    <div className="flex flex-col divide-y divide-foreground/10 border-y border-foreground/10 text-sm">
      {requests.map((request) => (
        <div key={request.id} className="flex flex-col gap-3 py-4">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{KIND_LABELS[request.kind]}</span>{' '}
            <span className="inline-flex h-6 items-center border border-foreground/20 px-2 text-xs font-semibold whitespace-nowrap">
              {STATUS_LABELS[request.status]}
            </span>
          </p>
          <dl className="flex flex-col gap-1 wrap-anywhere">
            {rowsOf(request).map((row) => (
              <div key={row.label} className="flex gap-4">
                <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">{row.label}</dt>
                <dd className="min-w-0 flex-1">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  </section>
);
